import React, { useState, useEffect, useMemo, useRef } from 'react'
import { ReactComponent as SearchBlue } from '../../../assets/svg/SearchBlue.svg'
import { ReactComponent as ChevronDownDuoIcon } from '../../../assets/svg/ChevronDownDuoIcon.svg'
import DonutChartSelfComparison from './DonutChartSelfComparison'
import { participationList } from '../../../constants/StudentAnalyticsConstants'
import Loading from '../../Loading'
import {
    getLiveResults,
    loadClassEngagementStats,
    loadContentForClass,
    loadContentForStudent,
    sendClassReminder,
    sendReminder,
    loadContentForCounsellor,
    loadYearAnalyticsMetric,
} from '../../../server/server'
import { format_day, getContentTypeIcon, useHasStudentInPath, useQuery } from '../../../utils/util'
import { engagementFilterMap, engagementFilterList } from '../../alerts/constants/AlertsConstants'
import i18n from '../../../i18n'
import { useTranslation } from 'react-i18next'
import ContentDetailsModal from './ContentDetailsModal'
import InfoButton from '../../common/InfoButton'
import ProgressBarAnalytics from './ProgressBarAnalytics'
import { useTourContext } from '../../../context/TourContext'
import { useUser } from '../../../auth'
import ShowContentResultsModal from '../../modals/ShowContentResultsModal'
import AssignFilter from '../../alerts/alerts/AssignFilter'
import { counsellorStats, contentActivities } from '../../analytics/counsellor/mocks/ParticipationData'

const SHOW_ROWS = 2

function ParticipationWidget({ daysFilter, student, isCounsellor, selectedUsersScope }) {
    const { t } = useTranslation()
    const query = useQuery()
    const isStudentAnalytics = useHasStudentInPath()
    const currentId = query.get('id')
    const requestVersion = useRef(0)
    const [limit, setLimit] = useState(SHOW_ROWS)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)
    const [percentage, setPercentage] = useState(0)
    const [searchText, setSearchText] = useState('')
    const showLoading = useMemo(() => (loading || !data || !data?.length) && !searchText, [loading, data, searchText])
    const [stats, setStats] = useState({})
    const { tourOpen } = useTourContext()
    const [total, setTotal] = useState(0)
    const [loadingMore, setLoadingMore] = useState(true)
    const [selectedFilter, setSelectedFilter] = useState(engagementFilterList[0])

    useEffect(() => {
        setLoading(true)
        loadData(0)
        if (!isStudentAnalytics && !isCounsellor) {
            loadClassStats()
        }
        return () => { requestVersion.current += 1 }
    }, [daysFilter, currentId, selectedUsersScope])

    const checkLoadType = useMemo(() => {
        if (isStudentAnalytics) {
            return loadContentForStudent
        } else if (isCounsellor) {
            return loadContentForCounsellor
        } else {
            return loadContentForClass
        }
    }, [isStudentAnalytics, isCounsellor])

    const loadData = async (loadedData = 0, searchText) => {
        const version = ++requestVersion.current
        const callFunction = checkLoadType
        const resp = await callFunction(isCounsellor ? selectedUsersScope : currentId, daysFilter.startDate, daysFilter.endDate, loadedData, searchText) //add selectedFilter
        if (version !== requestVersion.current) return
        if (resp?.status === 200) {
            const content = resp?.data?.content
            const percentage = resp?.data?.percentage
            const previousData = loadedData ? data : []
            const currentData = content
            setData([...previousData, ...currentData])
            if (loadedData === 0) setPercentage(percentage ?? 0)
            setTotal(resp?.data?.total_content)
            setLoading(false)
            setLoadingMore(false)
        } else {
            setData(null)
            setLoading(false)
        }
    }

    const loadClassStats = async () => {
        const resp = await loadClassEngagementStats(isCounsellor ? selectedUsersScope : currentId, daysFilter.startDate, daysFilter.endDate)
        if (resp.status === 200) {
            setStats(resp.data)
        }
    }

    const loadMore = () => {
        if (!loadingMore) {
            setLoadingMore(true)
            loadData(data?.length)
        }
    }

    const changeText = (e) => {
        setSearchText(e.target.value)
    }

    useEffect(() => {
        if (loading) return
        const delayDebounceFn = setTimeout(() => {
            loadData(0, searchText)
        }, 400)
        return () => clearTimeout(delayDebounceFn)
    }, [searchText])

    const usedData = useMemo(() => {
        return (data == null || !data?.length) && !searchText ? participationList : data
    }, [data, isCounsellor])

    const getTitle = () => {
        if (isStudentAnalytics) {
            return t('Participation in Activities')
        } else if (isCounsellor) {
            return t('Engagement in activities for selected student groups')
        } else return t('Class Engagement in Activities')
    }

    const getInfoText = () => {
        if (isStudentAnalytics) {
            return t(
                'This section displays the activities created for the student during the selected period. You can remind the student to complete activities or view detailed information about each activity.'
            )
        } else if (isCounsellor) {
            return t('Track how selected groups are engaging with activities, programs, and content.')
        } else
            return t(
                'This section displays the average participation of the class in all activities over the selected period. Use this to monitor engagement and identify areas where students may need more support or encouragement'
            )
    }

    const selectAssignedFilter = (filter) => {
        setLoading(true)
        setSelectedFilter(filter)
        loadData()
    }

    return (
        <div className="bg-white rounded-[24px] py-6 mt-4" data-tour="participation">
            <div className=" flex flex-row items-center justify-between px-6 gap-2">
                <div className="flex flex-row items-center gap-2">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">{getTitle()}</p>
                    <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px] ">
                        <p className="text-[13px] leading-[18px] font-normal text-strongGray whitespace-nowrap">{t(daysFilter.label)}</p>
                    </div>
                </div>
                <div className="flex flex-row items-center">
                    <button>
                        <InfoButton color="#5E639B" text={getInfoText()} />
                    </button>
                    {/* {isCounsellor && (
                        <div className="ml-5">
                            <AssignFilter
                                selectedFilter={selectedFilter}
                                setSelectedFilter={selectAssignedFilter}
                                list={engagementFilterList}
                                contentListMap={engagementFilterMap}
                                height={40}
                                width={142}
                            />
                        </div>
                    )} */}
                </div>
            </div>

            <div className="relative">
                <DataContainer
                    limit={limit}
                    data={usedData}
                    blur={showLoading}
                    percentage={percentage}
                    student={student}
                    setLimit={setLimit}
                    searchText={searchText}
                    changeText={changeText}
                    currentId={currentId}
                    isStudentAnalytics={isStudentAnalytics}
                    stats={stats}
                    loadMore={loadMore}
                    showMoreButton={total > data?.length}
                    isCounsellor={isCounsellor}
                    analyticsScope={selectedUsersScope?.yearGroup !== undefined ? selectedUsersScope : null}
                />
                {showLoading && <LoadingContainer showEmptyData={!loading && !data?.length && !searchText} />}
            </div>
        </div>
    )
}

export default ParticipationWidget

const DataContainer = ({
    data,
    limit,
    loadMore,
    blur,
    student,
    percentage,
    searchText,
    changeText,
    currentId,
    isStudentAnalytics,
    stats,
    showMoreButton = false,
    isCounsellor,
    analyticsScope,
}) => {
    const { t } = useTranslation()
    const { user } = useUser()
    const [selectedContent, setSelectedContent] = useState(null)
    const { tourOpen } = useTourContext()
    const [loadingItem, setLoadingItem] = useState(null)
    const [selectedContentAdvanced, setSelectedContentAdvanced] = useState(null)
    const [showResultsModal, setShowResultsModal] = useState(false)

    const checkIfStudentOrCounsellor = () => {
        if (isStudentAnalytics) return true
        if (isCounsellor) return true
        return false
    }

    const openResults = (item) => {
        if (isStudentAnalytics) setSelectedContent(item)
        else {
            setLoadingItem(item.id)
            loadData(item.id)
        }
    }

    const loadData = async (item) => {
        const resp = analyticsScope
            ? await loadYearAnalyticsMetric('content_results', analyticsScope, { content_id: item })
            : await getLiveResults({ user_id: user.id, content_id: item, refresh_data: false, class_id: currentId })
        if (resp.status === 200 && resp.data?.results?.length) {
            setSelectedContentAdvanced(resp.data.results[0])
            setShowResultsModal(true)
        }
        setLoadingItem(null)
    }

    const loadCounselorData = async (item) => {}

    const closeResultsModal = () => {
        setShowResultsModal(false)
        setSelectedContentAdvanced(null)
    }

    return (
        <div className={blur && !tourOpen ? 'blur' : ''}>
            <div className=" flex-1 px-6 mt-4">
                <div className={`flex flex-row items-center gap-4 border border-[#BEBECB] rounded-full h-[40px] px-[20px] `}>
                    <SearchBlue />
                    <input
                        type="text"
                        placeholder={t('Search for activities')}
                        className="bg-[transparent] placeholder:text-mediumGray leading-5 text-[16px] text-deepPurple font-medium w-full"
                        value={searchText}
                        onChange={changeText}
                        onFocus={() => {}}
                        onBlur={() => {}}
                    />
                </div>
            </div>
            {checkIfStudentOrCounsellor() && (
                <div className="flex flex-row items-center mt-4 px-6">
                    <DonutChartSelfComparison
                        percentage={percentage ?? 0}
                        size={50}
                        participation
                        backColor={percentage < 0.5 ? '#FD949B99' : '#6FCC8733'}
                        mainColor={percentage < 0.5 ? '#FC4C5999' : '#7DD193'}
                        textColor={percentage < 0.5 ? '#FC4C59' : '#7DD193'}
                        stroke={8}
                        completed={percentage * 100}
                        total={100}
                        showCheckIns={false}
                    />
                    <div className="flex flex-col gap-1 ml-2 flex-1">
                        <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple ">{t('Average participation rate')}</p>

                        {!isCounsellor && (
                            <p className="text-[14px] leading-[20px] font-medium text-darkenPurple ">
                                {t("Check out John's responses to app activities", { name: student?.first_name })}
                            </p>
                        )}
                    </div>
                </div>
            )}
            {!isStudentAnalytics && !isCounsellor && (
                <div className="">
                    <div className="flex flex-row items-center mt-4 mx-6 border-b border-b-[#BEBECE33] pb-[20px]">
                        <DonutChartSelfComparison
                            percentage={stats?.allContent?.avg_response}
                            size={80}
                            participation
                            backColor={stats?.allContent?.avg_response < 0.5 ? '#FD949B99' : '#6FCC8733'}
                            mainColor={stats?.allContent?.avg_response < 0.5 ? '#FC4C5999' : '#7DD193'}
                            textColor={stats?.allContent?.avg_response < 0.5 ? '#FC4C59' : '#7DD193'}
                            stroke={10}
                            completed={stats?.allContent?.avg_response * 100}
                            total={100}
                            showCheckIns={false}
                            additionalText={stats?.allContent?.total ?? 0}
                        />
                        <div className="flex flex-col ml-2 flex-1">
                            {!isCounsellor && (
                                <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple ">
                                    {t('Number of class engagements')}: {stats?.allContent?.total ?? 0}
                                </p>
                            )}

                            <p className="text-[14px] leading-[20px] font-medium text-darkenPurple ">
                                {t('Average class participation rate')}: {(stats?.allContent?.avg_response * 100)?.toFixed(0)}%
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-row items-center justify-between">
                        <div className="flex flex-row items-center mt-4 mx-6 border-b border-b-[#BEBECE33] pb-[20px]">
                            <DonutChartSelfComparison
                                percentage={stats?.surveys?.avg_response}
                                size={50}
                                participation
                                backColor={'#F1A71666'}
                                mainColor={'#F1A71699'}
                                textColor={'#F1A716'}
                                stroke={8}
                                completed={stats?.surveys?.total}
                                total={100}
                                showCheckIns={false}
                                showPercentage={false}
                            />
                            <div className="flex flex-col ml-2 flex-1">
                                <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple ">
                                    {t('Number of surveys')}: {stats?.surveys?.total}
                                </p>

                                <p className="text-[14px] leading-[20px] font-medium text-darkenPurple ">
                                    {t('Avg. participation rate')}: {(stats?.surveys?.avg_response * 100)?.toFixed(0)}%
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-row items-center mt-4 mx-6 border-b border-b-[#BEBECE33] pb-[20px]">
                            <DonutChartSelfComparison
                                percentage={stats?.polls?.avg_response}
                                size={50}
                                participation
                                backColor={'#6FCC8766'}
                                mainColor={'#6FCC8799'}
                                textColor={'#6FCC87'}
                                stroke={8}
                                completed={stats?.polls?.total}
                                total={100}
                                showCheckIns={false}
                                showPercentage={false}
                            />
                            <div className="flex flex-col ml-2 flex-1">
                                <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple ">
                                    {t('Number of polls')}: {stats?.polls?.total}
                                </p>

                                <p className="text-[14px] leading-[20px] font-medium text-darkenPurple ">
                                    {t('Avg. participation rate')}: {(stats?.polls?.avg_response * 100)?.toFixed(0)}%
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-row items-center mt-4 mx-6 border-b border-b-[#BEBECE33] pb-[20px]">
                            <DonutChartSelfComparison
                                percentage={stats?.questions?.avg_response}
                                size={50}
                                participation
                                backColor={'#7B7AC766'}
                                mainColor={'#7B7AC799'}
                                textColor={'#7B7AC7'}
                                stroke={8}
                                completed={stats?.questions?.total}
                                total={100}
                                showCheckIns={false}
                                showPercentage={false}
                            />
                            <div className="flex flex-col ml-2 flex-1">
                                <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple ">
                                    {t('Number of questions')}: {stats?.questions?.total}
                                </p>

                                <p className="text-[14px] leading-[20px] font-medium text-darkenPurple ">
                                    {t('Avg. participation rate')}: {(stats?.questions?.avg_response * 100)?.toFixed(0)}%
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <p className="text-[14px] leading-[20px] font-medium text-darkenPurple mt-4 px-6">
                {isStudentAnalytics ? t('Check activity responses here') : t('Check class activity responses here')}
            </p>
            <div className="flex flex-col mt-4 border-t border-t-[#BEBECE33]">
                {data.map((item, index) => (
                    <div className={`flex flex-row justify-between px-6 py-3 bg-white border-b border-[#BEBECE33]`} key={index}>
                        <div className="flex flex-row">
                            <div>{getContentTypeIcon(item.type)}</div>
                            <div className="flex flex-col ml-[9px] ">
                                <p className="text-[14px] leading-[20px] font-bold text-darkenPurple capitalize">{`${t(item.type)}`}</p>
                                <p className="text-[12px] leading-[20px] font-medium text-darkenPurple mt-1">
                                    {format_day(new Date(item?.publicationDate), true, i18n.language)} {t('by')} {item?.user_info?.first_name} {item?.user_info?.last_name}
                                </p>
                                {!isStudentAnalytics && <ClassParticipation item={item} />}

                                <div className="flex flex-row gap-2 mt-2" {...(index === 0 ? { 'data-tour': 'participation-actions' } : {})}>
                                    <button
                                        className={`bg-[#F8F8FB] rounded-full px-[15px] py-[5px]  border border-[#ECEBF8] transition-all min-w-[76px] items-center justify-center flex ${
                                            loadingItem === item.id ? '' : 'hover:border-[#5E639B]'
                                        }`}
                                        onClick={() => openResults(item)}
                                        disabled={loadingItem === item.id}
                                    >
                                        {loadingItem === item.id ? <Loading /> : <p className="text-[14px] leading-[20px] font-semibold text-deepPurple">{t('Details')}</p>}
                                    </button>
                                    {!analyticsScope && (!item?.answer_data || ('response_percentage' in item && item?.response_percentage !== 1)) && (
                                        <RemindButton item={item} currentId={currentId} />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col bg-[#F8F8FB] p-2 rounded-[8px]  gap-[3px] ml-6 flex-1 max-w-[60%]">
                            <p className=" font-bold text-[14px] leading-5 text-[#413F5E] ">{item?.name ?? item?.title}</p>
                            {item?.description && <pre className=" font-medium text-[14px] leading-5 text-[#413F5E] line-clamp-2">{item?.description}</pre>}
                        </div>
                    </div>
                ))}
                {!data?.length && !!searchText && (
                    <div className="flex items-center justify-center h-[100px]">
                        <p className="text-center text-[14px] text-mediumGray">{t('No data to show based on your search')}</p>
                    </div>
                )}
            </div>
            {!blur && showMoreButton && (
                <div className="flex flex-row justify-end flex-1 mt-2 mx-6 ">
                    <button
                        className="flex flex-row items-center justify-center gap-2 border border-[#ECEBF8] hover:border-darkenPurple transition rounded-full h-[34px] px-4 mt-4 bg-[#F8F8FB]"
                        onClick={() => loadMore()}
                    >
                        <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{t('Load More')}</p>
                        <div>
                            <ChevronDownDuoIcon />
                        </div>
                    </button>
                </div>
            )}
            <ContentDetailsModal modalIsOpen={!!selectedContent?.id} content={selectedContent} closeModal={() => setSelectedContent(null)} />
            <ShowContentResultsModal
                content={selectedContentAdvanced}
                showRun={showResultsModal}
                closeRun={closeResultsModal}
                setRefresh={() => {}}
                deleteContent={() => {}}
                hideControlDetails
                classId={currentId}
                analyticsScope={analyticsScope}
            />
        </div>
    )
}

const RemindButton = ({ item, currentId }) => {
    const { t } = useTranslation()
    const isStudentAnalytics = useHasStudentInPath()
    const [state, setState] = useState('init') // init, loading, success, error

    const trigger = async () => {
        setState('loading')
        const callFunction = isStudentAnalytics ? sendReminder : sendClassReminder
        const resp = await callFunction(currentId, item)
        if (resp.status !== 200) {
            setState('error')
            return
        } else {
            setState('success')
        }
        setTimeout(() => {
            setState('init')
        }, 2000)
    }

    return (
        <button
            className={`bg-[#F8F8FB] rounded-full px-[15px] py-[5px]  border border-[#ECEBF8] min-w-[83px] justify-center items-center flex ${
                state !== 'init' ? '' : 'hover:border-[#5E639B]'
            }  transition-all`}
            onClick={trigger}
            disabled={state !== 'init'}
        >
            {state === 'init' && <p className="text-[14px] leading-[20px] font-semibold text-deepPurple text-center">{t('Remind')}</p>}
            {state === 'loading' && <Loading size={20} stroke={6} />}
            {state === 'success' && <p className="text-[14px] leading-[20px] font-semibold text-deepPurple text-center">{t('Reminded')}</p>}
        </button>
    )
}

const LoadingContainer = ({ limit, showEmptyData = false }) => {
    const { t } = useTranslation()
    return (
        <div>
            {showEmptyData ? (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <p className="text-[16px] leading-[25px] font-bold text-mudPurple ">{t('No data found')}</p>
                        <p className="text-[14px] leading-[25px] font-medium text-mudPurple text-center">
                            {t('This element will be updated once enough app-activity has been recorded')}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10">
                    <Loading size={50} stroke={5} />
                </div>
            )}
        </div>
    )
}

const ClassParticipation = ({ item }) => {
    const { t } = useTranslation()
    const percentage = Math.round(item?.response_percentage * 100)
    return (
        <div className="flex flex-col gap-1 w-[140px] mt-1">
            <p className="text-[12px] leading-[20px] font-medium text-darkenPurple ">
                {t('Class participation')} <span className={`${percentage >= 50 ? 'text-[#6FCC87]' : 'text-[#FC4C59]'}`}>{percentage.toFixed(0)}%</span>
            </p>
            <ProgressBarAnalytics frontColor={`${percentage >= 50 ? '#6FCC8799' : '#FC4C5999'}`} restColor={`#413F5E2B`} completed={percentage} height={8} />
        </div>
    )
}
